import { useState } from 'react';
import { Tag, Space, Button, Modal, Form, Input, Select, message, InputNumber } from 'antd';
import { PlusOutlined, EditOutlined } from '@ant-design/icons';
import { ResourceList } from '@/components/ResourceList';
import {
  createAdAccount,
  listAdAccounts,
  updateAdAccount,
  type AdAccount,
  type AdPlatform,
} from '@/api/ad-accounts';
import { formatCNY, formatDateTime } from '@/utils/format';

const statusColor: Record<string, string> = {
  active: 'green',
  paused: 'gold',
  archived: 'default',
};

const platformOptions: Array<{ value: AdPlatform; label: string }> = [
  { value: 'oceanengine', label: '巨量引擎' },
  { value: 'wechat_ads', label: '微信广告' },
  { value: 'douyin', label: '抖音' },
  { value: 'tencent_ads', label: '腾讯广告' },
];

export function AdAccountsListPage() {
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState<AdAccount | null>(null);
  const [form] = Form.useForm();
  const [refreshKey, setRefreshKey] = useState(0);

  const onEdit = (row: AdAccount) => {
    setEditing(row);
    form.setFieldsValue({
      name: row.name,
      platform: row.platform,
      external_account_id: row.external_account_id,
      daily_budget_cents: row.daily_budget_cents,
      notes: row.notes,
    });
    setOpen(true);
  };

  const onNew = () => {
    setEditing(null);
    form.resetFields();
    setOpen(true);
  };

  const onSubmit = async () => {
    const values = await form.validateFields();
    try {
      if (editing) {
        await updateAdAccount(editing.id, values);
        message.success('已更新');
      } else {
        await createAdAccount(values);
        message.success('已新增');
      }
      setOpen(false);
      setRefreshKey((k) => k + 1);
    } catch (e) {
      message.error(e instanceof Error ? e.message : '保存失败');
    }
  };

  return (
    <>
      <ResourceList<AdAccount>
        key={refreshKey}
        title="投放账号"
        description="管理巨量 / 微信广告 / 抖音 等账号；auth_blob 仅在创建/轮换时写入，不在列表返回。"
        rowKey="id"
        pagination="offset"
        filters={[
          { key: 'q', label: '关键字', type: 'text', placeholder: '账号名或外部 id' },
          {
            key: 'platform',
            label: '平台',
            type: 'select',
            options: platformOptions.map((p) => ({ value: p.value, label: p.label })),
          },
          {
            key: 'status',
            label: '状态',
            type: 'select',
            options: [
              { value: 'active', label: '运行中' },
              { value: 'paused', label: '已暂停' },
              { value: 'archived', label: '已归档' },
            ],
          },
        ]}
        fetcher={({ filters, page, pageSize }) =>
          listAdAccounts({ ...filters, page, page_size: pageSize } as never)
        }
        actions={[
          { label: '新增账号', icon: <PlusOutlined />, onClick: onNew },
        ]}
        columns={[
          { title: '名称', dataIndex: 'name' },
          {
            title: '平台',
            dataIndex: 'platform',
            render: (v) => <Tag color="geekblue">{v}</Tag>,
          },
          { title: '外部 ID', dataIndex: 'external_account_id', render: (v) => v || '-' },
          {
            title: '日预算',
            dataIndex: 'daily_budget_cents',
            align: 'right',
            render: (v) => formatCNY(v),
          },
          {
            title: '状态',
            dataIndex: 'status',
            render: (v) => <Tag color={statusColor[v] || 'default'}>{v}</Tag>,
          },
          { title: '更新时间', dataIndex: 'updated_at', render: (v) => formatDateTime(v) },
          {
            title: '操作',
            key: 'actions',
            width: 100,
            render: (_, row) => (
              <Space>
                <Button size="small" icon={<EditOutlined />} onClick={() => onEdit(row)}>
                  编辑
                </Button>
              </Space>
            ),
          },
        ]}
      />

      <Modal
        open={open}
        title={editing ? '编辑投放账号' : '新增投放账号'}
        onCancel={() => setOpen(false)}
        onOk={onSubmit}
        okText="保存"
      >
        <Form form={form} layout="vertical">
          <Form.Item
            name="name"
            label="账号名"
            rules={[{ required: true, message: '请输入名称' }, { max: 64 }]}
          >
            <Input maxLength={64} />
          </Form.Item>
          <Form.Item
            name="platform"
            label="平台"
            rules={[{ required: true, message: '请选择平台' }]}
          >
            <Select options={platformOptions} disabled={!!editing} />
          </Form.Item>
          <Form.Item name="external_account_id" label="平台账号 ID">
            <Input placeholder="例如 oceanengine 的 advertiser_id" />
          </Form.Item>
          <Form.Item name="daily_budget_cents" label="日预算 (分)">
            <InputNumber min={0} style={{ width: '100%' }} />
          </Form.Item>
          <Form.Item name="notes" label="备注">
            <Input.TextArea rows={2} maxLength={300} />
          </Form.Item>
        </Form>
      </Modal>
    </>
  );
}
